import React, { FC, useState, useRef, useEffect, useCallback } from 'react';
import { Form, Field as RFFField } from 'react-final-form';
import type { FormApi } from 'final-form';
import { Alert, Button } from '@manulife/mux';
import { StepProps, CreateAgencyFormData, SelectOption } from '../types';
import { MUXTextInput, MUXSelect, MUXDatePicker, toCalendarDate } from '../components/FormWrappers';
import { isDateOnOrAfter, isNonEmptyString, isValidDateValue, isValidEmail, isValidLicenseStatus, isValidProvince } from '../validation';
import { PROVINCES } from '@trip-ux/constants';
import { Contacts, DistributionLicense } from '@trip-ux/redux-store';

// --- Types ---

interface Step4FormValues {
  contactEmail?: string;
  contactName?: string;
  contactType?: string;
  contactPhone?: string;
  [key: string]: unknown;
}

// --- Constants ---

const CONTACT_TYPE_OPTIONS: SelectOption[] = [
  { label: 'Licensing', value: 'LICENSING' },
  { label: 'General Inquiry', value: 'GENERAL_INQUIRY' },
  { label: 'Operations', value: 'OPERATIONS' },
  { label: 'Manager', value: 'MANAGER' },
  { label: 'Owner', value: 'OWNER' },
  { label: 'Accounting', value: 'ACCOUNTING' }
];

const PROVINCE_OPTIONS: SelectOption[] = Object.entries(PROVINCES).map(([code, label]) => ({
  label,
  value: code
}));

const LICENSE_STATUS_OPTIONS: SelectOption[] = [
  { label: 'Authorized', value: 'AUTHORIZED' },
  { label: 'In Progress', value: 'IN_PROGRESS' },
  { label: 'Renewal Pending', value: 'RENEWAL_PENDING' },
  { label: 'Not Authorized', value: 'NOT_AUTHORIZED' }
];

const DEFAULT_EXPIRY = '2099-12-31';

// --- Validation Helpers ---

const validateLicenseEntry = (
  values: Step4FormValues,
  index: number,
  errors: Record<string, string>
): void => {
  const province = values[`licensedProvince_${index}`] as string | undefined;
  const status = values[`licenseStatus_${index}`] as string | undefined;
  const effectiveDate = values[`licenseEffectiveDate_${index}`];
  const expiryDate = values[`licenseExpiryDate_${index}`];

  if (isNonEmptyString(province) && !isValidProvince(province)) {
    errors[`licensedProvince_${index}`] = 'Invalid licensed province';
  }
  if (isNonEmptyString(status) && !isValidLicenseStatus(status)) {
    errors[`licenseStatus_${index}`] = 'Invalid license status';
  }
  if (effectiveDate && !isValidDateValue(effectiveDate)) {
    errors[`licenseEffectiveDate_${index}`] = 'Invalid license effective date';
  }
  if (expiryDate && !isValidDateValue(expiryDate)) {
    errors[`licenseExpiryDate_${index}`] = 'Invalid license expiry date';
  }
  if (effectiveDate && expiryDate && !isDateOnOrAfter(expiryDate, effectiveDate)) {
    errors[`licenseExpiryDate_${index}`] = 'License expiry date must be on or after effective date';
  }
};

const validateContactEmail = (
  email: string | undefined,
  errors: Record<string, string>
): void => {
  if (!isNonEmptyString(email)) {
    errors.contactEmail = 'Contact email is required';
  } else if (!isValidEmail(email)) {
    errors.contactEmail = 'Invalid email format';
  }
};

// --- Data Builder Helpers ---

const buildInitialValues = (formData: CreateAgencyFormData): Step4FormValues => {
  const existingContacts = formData.contacts || [];
  const existingLicenses = formData.distributionLicenses || [];

  const values: Step4FormValues = {
    contactEmail: existingContacts[0]?.email || '',
    contactName: existingContacts[0]?.contactName || '',
    contactType: existingContacts[0]?.contactType || 'LICENSING',
    contactPhone: existingContacts[0]?.phone || ''
  };

  if (existingLicenses.length === 0) {
    values.licensedProvince_0 = '';
    values.licenseNumber_0 = '';
    values.licenseStatus_0 = 'AUTHORIZED';
    values.licenseEffectiveDate_0 = '';
    values.licenseExpiryDate_0 = '';
    values.legalName_0 = '';
    return values;
  }

  existingLicenses.forEach((license, index) => {
    values[`licensedProvince_${index}`] = license.licensedProvince || '';
    values[`licenseNumber_${index}`] = license.licenseNumber || '';
    values[`licenseStatus_${index}`] = license.status || 'AUTHORIZED';
    values[`licenseEffectiveDate_${index}`] = toCalendarDate(license.effectiveDate);
    values[`licenseExpiryDate_${index}`] = toCalendarDate(license.expiredDate);
    values[`legalName_${index}`] = license.legalName || '';
  });

  return values;
};

const buildContacts = (values: Step4FormValues): Contacts[] => {
  if (!values.contactEmail) {
    return [];
  }
  return [{
    isMain: true,
    contactName: values.contactName || '',
    contactType: values.contactType || 'LICENSING',
    email: values.contactEmail,
    phone: values.contactPhone || '',
    hoursOfOperations: '',
    hoursOfOperationsFr: '',
    effectiveDate: new Date(),
    expiryDate: new Date(DEFAULT_EXPIRY)
  }];
};

const buildLicenseFromValues = (values: Step4FormValues, index: number): DistributionLicense | null => {
  const province = values[`licensedProvince_${index}`] as string | undefined;
  if (!province) {
    return null;
  }
  return {
    id: index + 1,
    licensedProvince: province,
    licenseNumber: (values[`licenseNumber_${index}`] as string) || '',
    status: (values[`licenseStatus_${index}`] as string) || 'AUTHORIZED',
    effectiveDate: values[`licenseEffectiveDate_${index}`] ? new Date(values[`licenseEffectiveDate_${index}`] as string) : new Date(),
    expiredDate: values[`licenseExpiryDate_${index}`] ? new Date(values[`licenseExpiryDate_${index}`] as string) : undefined,
    legalName: (values[`legalName_${index}`] as string) || '',
    screeningStatus: 'NA'
  };
};

const buildDistributionLicenses = (values: Step4FormValues, count: number): DistributionLicense[] => {
  const licenses: DistributionLicense[] = [];
  for (let i = 0; i < count; i++) {
    const license = buildLicenseFromValues(values, i);
    if (license) {
      licenses.push(license);
    }
  }
  return licenses;
};

// --- Sub-Components ---

const ContactFields: FC = () => (
  <fieldset className="mux-mb-6">
    <legend className="mux-text-lg mux-font-semibold mux-mb-4">Primary Contact</legend>
    <div className="mux-grid mux-grid-cols-1 tablet:mux-grid-cols-2 mux-gap-4">
      <div className="mux-col-span-1">
        <RFFField name="contactName" component={MUXTextInput} label="Contact Name" type="text" />
      </div>
      <div className="mux-col-span-1">
        <RFFField name="contactType" component={MUXSelect} label="Contact Type" options={CONTACT_TYPE_OPTIONS} />
      </div>
      <div className="mux-col-span-1">
        <RFFField name="contactEmail" component={MUXTextInput} label="Email" type="email" required />
      </div>
      <div className="mux-col-span-1">
        <RFFField name="contactPhone" component={MUXTextInput} label="Phone" type="tel" />
      </div>
    </div>
  </fieldset>
);

interface LicenseEntryCardProps {
  index: number;
  showRemove: boolean;
  onRemove: (index: number) => void;
}

const LicenseEntryCard: FC<LicenseEntryCardProps> = ({ index, showRemove, onRemove }) => {
  const handleRemove = useCallback(() => {
    onRemove(index);
  }, [index, onRemove]);

  return (
    <div className="mux-mb-6 mux-p-4 mux-border mux-border-gray-300 mux-rounded">
      <div className="mux-flex mux-justify-between mux-items-center mux-mb-4">
        <h3 className="mux-text-md mux-font-semibold">License {index + 1}</h3>
        {showRemove && (
          <Button variant="tertiary" onClick={handleRemove}>
            Remove
          </Button>
        )}
      </div>
    <div className="mux-grid mux-grid-cols-1 tablet:mux-grid-cols-2 mux-gap-4">
      <div className="mux-col-span-1">
        <RFFField name={`licensedProvince_${index}`} component={MUXSelect} label="Licensed Province" options={PROVINCE_OPTIONS} />
      </div>
      <div className="mux-col-span-1">
        <RFFField name={`licenseNumber_${index}`} component={MUXTextInput} label="License Number" type="text" />
      </div>
      <div className="mux-col-span-1">
        <RFFField name={`licenseStatus_${index}`} component={MUXSelect} label="License Status" options={LICENSE_STATUS_OPTIONS} />
      </div>
      <div className="mux-col-span-1">
        <RFFField name={`legalName_${index}`} component={MUXTextInput} label="Legal Name for License" type="text" />
      </div>
      <div className="mux-col-span-1">
        <RFFField name={`licenseEffectiveDate_${index}`} component={MUXDatePicker} label="License Effective Date" />
      </div>
      <div className="mux-col-span-1">
        <RFFField name={`licenseExpiryDate_${index}`} component={MUXDatePicker} label="License Expiry Date" />
      </div>
    </div>
  </div>
  );
};

// --- Main Component ---

const Step4ContactsLicenses: FC<StepProps> = ({ formData, onNext, formRef }) => {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const nextLicenseIdRef = useRef(1);
  const [licenseIds, setLicenseIds] = useState<number[]>(() => {
    const count = formData.distributionLicenses?.length || 1;
    const ids = Array.from({ length: count }, (_, i) => i);
    nextLicenseIdRef.current = count;
    return ids;
  });
  const formInstanceRef = useRef<FormApi<Step4FormValues> | null>(null);
  const initialValuesRef = useRef<Step4FormValues | null>(null);

  if (!initialValuesRef.current) {
    initialValuesRef.current = buildInitialValues(formData);
  }

  const validateStep4 = (values: Step4FormValues): Record<string, string> => {
    const newErrors: Record<string, string> = {};
    validateContactEmail(values.contactEmail, newErrors);
    for (let i = 0; i < licenseIds.length; i++) {
      validateLicenseEntry(values, i, newErrors);
    }
    return newErrors;
  };

  const handleSubmit = (values: Step4FormValues) => {
    const newErrors = validateStep4(values);
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});

    onNext({
      contacts: buildContacts(values),
      distributionLicenses: buildDistributionLicenses(values, licenseIds.length)
    } as Partial<CreateAgencyFormData>);
  };

  const handleAddLicense = useCallback(() => {
    const newId = nextLicenseIdRef.current;
    nextLicenseIdRef.current += 1;
    setLicenseIds(prev => [...prev, newId]);
  }, []);

  const handleAlertClose = useCallback(() => {
    setErrors({});
  }, []);

  const handleRemoveLicenseAt = useCallback((index: number) => {
    if (licenseIds.length <= 1) {
      return;
    }
    if (formInstanceRef.current) {
      const form = formInstanceRef.current;
      form.change(`licensedProvince_${index}`, '');
      form.change(`licenseNumber_${index}`, '');
      form.change(`licenseStatus_${index}`, 'AUTHORIZED');
      form.change(`licenseEffectiveDate_${index}`, '');
      form.change(`licenseExpiryDate_${index}`, '');
      form.change(`legalName_${index}`, '');
    }
    setLicenseIds(prev => prev.filter((_, i) => i !== index));
  }, [licenseIds.length]);

  useEffect(() => {
    if (formRef && formInstanceRef.current) {
      formRef({ submit: () => formInstanceRef.current?.submit() });
    }
  }, [formRef]);

  return (
    <Form
      onSubmit={handleSubmit}
      validate={validateStep4}
      initialValues={initialValuesRef.current}
      render={({ handleSubmit: formSubmit, form }) => {
        formInstanceRef.current = form;
        return (
          <form onSubmit={formSubmit} className="step-form">

            {Object.keys(errors).length > 0 && (
              <Alert severity="alert" onClose={handleAlertClose}>
                Please fix the errors below before proceeding.
              </Alert>
            )}

            <ContactFields />

            <fieldset className="mux-mb-6">
              <div className="mux-flex mux-justify-between mux-items-center mux-mb-4">
                <legend className="mux-text-lg mux-font-semibold">Distribution Licenses (Optional)</legend>
                <Button variant="tertiary" onClick={handleAddLicense}>+ Add License</Button>
              </div>
              {licenseIds.map((licenseId, index) => (
                <LicenseEntryCard
                  key={`license-${licenseId}`}
                  index={index}
                  showRemove={licenseIds.length > 1}
                  onRemove={handleRemoveLicenseAt}
                />
              ))}
            </fieldset>
          </form>
        );
      }}
    />
  );
};

export default Step4ContactsLicenses;
